import {
  AssetCategory,
  resolveAssetCategoryLabel,
  resolvePredefinedAssetCategoryName
} from '@simOn/asset-categories/models';
import { AssetHyperlinks, AssetMonitor, TAsset, TRecurringEventTime } from '@simOn/asset/modifying/models';
import { EAssetCategoryKeys, EAssetCategoryValues } from '@simOn/common/categories';
import { IPagination, IPaginationRequest } from '@simOn/common/http';
import { TransformationInterface } from '@simOn/common/matterport';
import { Attachment, LastEditedType } from '@simOn/common/models';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { UserRoleType } from '@simOn/privileges/element/models';
import { EnumValueGetter } from '@simOn/utils/models';

export interface AssetResponseInterface {
  asset: AssetCategoryIconType;
  id: string;
  name: string;
  apartmentId: string;
  // apartment?: ApartmentInterface;
  roomId: string;
  // room?: RoomInterface;
  categoryId?: string | null;
  category?: AssetCategory | null | undefined;
  textNote: string;
  hyperlinks: AssetHyperlinks[];
  attachedImages: Attachment[];
  attachedDocuments: Attachment[];
  events: AssetEvent[];
  transformation: TransformationInterface | undefined;
  positionDeleted?: boolean;
  matterTagContent: string | undefined;
  roomName?: string;
  containsBlockedAttachments: boolean;
}

export interface UpdateAsset {
  id: string;
  roomId: string;
  category?: string;
  name: string;
  textNote?: string;
  uploadedFiles?: string[];
  hyperlinks?: AssetHyperlinks[];
  events?: AssetEvent[];
  transformation?: TransformationInterface;
  positionDeleted?: boolean;
  matterTagContent?: string;
}

export type AssetIconsType =
  | 'Documents'
  | 'Decoration'
  | 'Construction'
  | 'FinishingMaterials'
  | 'Multimedia'
  | 'Plants'
  | 'Plumbing'
  | 'Hobby'
  | 'Electrical'
  | 'Furniture'
  | 'Other';

export interface AssetsCategoryInterface {
  name: EAssetCategoryKeys;
  icon: string;
}

export const AssetsCategory: AssetsCategoryInterface[] = [
  { name: 'Multimedia', icon: 'Circle_Multimedia.svg' },
  { name: 'HomeAppliances', icon: 'Circle_HomeAppliances.svg' },
  { name: 'OfficeEquipment', icon: 'Circle_OfficeEquipment.svg' },
  { name: 'Furniture', icon: 'Circle_Furniture.svg' },
  { name: 'Decoration', icon: 'Circle_Decoration.svg' },
  { name: 'LightingSystems', icon: 'Circle_LightingSystems.svg' },
  { name: 'Plants', icon: 'Circle_Plants.svg' },
  { name: 'Documents', icon: 'Circle_Documents.svg' },
  { name: 'Electrical', icon: 'Circle_Electrical.svg' },
  { name: 'Security', icon: 'Circle_Security.svg' },
  { name: 'FireSafety', icon: 'Circle_FireSafety.svg' },
  { name: 'Plumbing', icon: 'Circle_Plumbing.svg' },
  { name: 'SanitaryAndHygiene', icon: 'Circle_SanitaryAndHygiene.svg' },
  { name: 'HVACSystems', icon: 'Circle_HVACSystems.svg' },
  { name: 'RES', icon: 'Circle_RES.svg' },
  { name: 'Hobby', icon: 'Circle_Hobby.svg' },
  { name: 'Transport', icon: 'Circle_Transport.svg' },
  { name: 'Education', icon: 'Circle_Education.svg' },
  { name: 'Tools', icon: 'Circle_Tools.svg' },
  { name: 'Garden', icon: 'Circle_Garden.svg' },
  { name: 'Machines', icon: 'Circle_Machines.svg' },
  { name: 'SpareParts', icon: 'Circle_SpareParts.svg' },
  { name: 'Pets', icon: 'Circle_Pets.svg' },
  { name: 'Kids', icon: 'Circle_Kids.svg' },
  { name: 'FinishingMaterials', icon: 'Circle_FinishingMaterials.svg' },
  { name: 'Construction', icon: 'Circle_Construction.svg' },
  { name: 'Other', icon: 'Circle_Other.svg' }
];

export interface AssetsTagEntityInterface {
  [key: string]: string;
}

export interface AssetCreateInterface {
  id?: string; // for update action
  //do sprawdzenia zgodnie z starym interface
  apartmentId?: string;
  roomId: string;
  name: string;
  textNote?: string;
  uploadedFiles?: any;
  categoryId?: string | null;
  category?: AssetCategory | null;

  position?: {
    x: number;
    y: number;
    z: number;
  };
  matterTagContent?: string;
}

export type AssetEvent = {
  id: string; //Sprawdzić czy nie psuje
  dateTime: string;
  content: string;
  recurringEventType?: TRecurringEventTime;
};

export interface GetAssetsQueryParamsInterface {
  roomGuid?: string;
  FetchLong?: boolean;
  Category?: EAssetCategoryValues;
}

export interface AssetHyperlinksInterface {
  assetGuid: string;
  hyperlinks: AssetHyperlinks[];
}

export interface HyperlinkInterface {
  link: string;
  type: string;
}

export interface AddNewAssetModalResultInterface {
  action: AddNewAssetModalActions;
  payload?: AssetResponseInterface;
}

export type AddNewAssetModalActions = 'save' | 'close';

export type AssetModalActions =
  'createAssetMarker' | 'deleteAssetMarker' | 'goToAssetTag' | 'goToAssetDetails' | 'close';
export interface AssetModalResultInterface {
  action: AssetModalActions;
  payload?: {
    asset?: AssetResponseInterface;
  };
}

export interface AssetsListFilterFormInterface {
  assetCategory: string;
  search: string;
}

export interface AssetFormValueInterface {
  name: string;
  room: string;
  assetCategory: string;
}

export const AssetCategoryCollection: AssetCategoryCollectionInterface[] = [
  { value: 'Multimedia', name: $localize`:@@MULTIMEDIA:Multimedia`, icon: 'multimedia' },
  { value: 'HomeAppliances', name: $localize`:@@HOME_APPLIANCES:Home Appliances`, icon: 'homeAppliances' },
  { value: 'OfficeEquipment', name: $localize`:@@OFFICE_EQUIPMENT:Office Equipment`, icon: 'officeEquipment' },
  { value: 'Furniture', name: $localize`:@@FURNITURE:Furniture`, icon: 'furniture' },
  { value: 'Decoration', name: $localize`:@@DECORATION:Decoration`, icon: 'decoration' },
  { value: 'LightingSystems', name: $localize`:@@LIGHTING_SYSTEMS:Lighting Systems`, icon: 'lightingSystems' },
  { value: 'Plants', name: $localize`:@@PLANTS:Plants`, icon: 'plants' },
  { value: 'Documents', name: $localize`:@@DOCUMENTS:Documents`, icon: 'documents' },
  { value: 'Electrical', name: $localize`:@@ELECTRICAL:Electrical`, icon: 'electrical' },
  { value: 'Security', name: $localize`:@@SECURITY:Security`, icon: 'security' },
  { value: 'FireSafety', name: $localize`:@@FIRE_SAFETY:Fire Safety`, icon: 'fireSafety' },
  { value: 'Plumbing', name: $localize`:@@PLUMBING:Plumbing`, icon: 'plumbing' },
  {
    value: 'SanitaryAndHygiene',
    name: $localize`:@@SANITARY_AND_HYGIENE:Sanitary and Hygiene`,
    icon: 'sanitaryAndHygiene'
  },
  { value: 'HVACSystems', name: $localize`:@@HVAC_SYSTEMS:HVAC Systems`, icon: 'hvacSystems' },
  { value: 'RES', name: $localize`:@@RES:RES (Renewable Energy Sources)`, icon: 'res' },
  { value: 'Hobby', name: $localize`:@@HOBBY:Hobby`, icon: 'hobby' },
  { value: 'Transport', name: $localize`:@@TRANSPORT:Transport`, icon: 'transport' },
  { value: 'Education', name: $localize`:@@EDUCATION:Education`, icon: 'education' },
  { value: 'Tools', name: $localize`:@@TOOLS:Tools`, icon: 'tools' },
  { value: 'Garden', name: $localize`:@@GARDEN:Garden`, icon: 'garden' },
  { value: 'Machines', name: $localize`:@@MACHINES:Machines`, icon: 'machines' },
  { value: 'SpareParts', name: $localize`:@@SPARE_PARTS:Spare Parts`, icon: 'spareParts' },
  { value: 'Pets', name: $localize`:@@PETS:Pets`, icon: 'pets' },
  { value: 'Kids', name: $localize`:@@KIDS:Kids`, icon: 'kids' },
  {
    value: 'FinishingMaterials',
    name: $localize`:@@FINISHING_MATERIALS:Finishing Materials`,
    icon: 'finishingMaterials'
  },
  { value: 'Construction', name: $localize`:@@CONSTRUCTION:Construction`, icon: 'construction' },
  { value: 'Other', name: $localize`:@@OTHER:Other`, icon: 'assetOtherIcon' }
];

export const AssetCategoryEntity: AssetCategoryEntityInterface = AssetCategoryCollection.reduce(
  (acc: AssetCategoryEntityInterface, el: AssetCategoryCollectionInterface) => {
    acc[el.value] = el;
    return acc;
  },
  {} as AssetCategoryEntityInterface
);

/** Maps a backend category object to the legacy collection item (name + `sim-icon` identifier). */
export function toAssetCategoryItem(category: AssetCategory | null | undefined): AssetCategoryCollectionInterface {
  const predefinedName = category ? resolvePredefinedAssetCategoryName(category.predefinedCategory) : null;
  const fallback =
    AssetCategoryEntity[predefinedName && predefinedName !== 'None' ? predefinedName : 'Other'] ||
    AssetCategoryEntity['Other'];
  if (!category) return fallback;

  return {
    ...fallback,
    name: resolveAssetCategoryLabel(category),
    icon: (category.icon ?? fallback.icon) as AssetCategoryCollectionInterface['icon']
  };
}

export interface AssetCategoryCollectionInterface {
  value: EAssetCategoryKeys;
  name: string;
  icon: AssetCategoryIconType;
}

export type AssetCategoryEntityInterface = {
  [key in EAssetCategoryKeys]: AssetCategoryCollectionInterface;
};

export type AssetCategoryIconType =
  | 'multimedia'
  | 'homeAppliances'
  | 'officeEquipment'
  | 'furniture'
  | 'decoration'
  | 'lightingSystems'
  | 'plants'
  | 'documents'
  | 'electrical'
  | 'security'
  | 'fireSafety'
  | 'plumbing'
  | 'sanitaryAndHygiene'
  | 'hvacSystems'
  | 'res'
  | 'hobby'
  | 'transport'
  | 'education'
  | 'tools'
  | 'garden'
  | 'machines'
  | 'spareParts'
  | 'pets'
  | 'kids'
  | 'finishingMaterials'
  | 'construction'
  | 'assetOtherIcon';

export type AssetCategoryKeyType = EAssetCategoryKeys;
export type AssetCategoryValueType = EAssetCategoryValues;

export interface AssetMatterTagInterface {
  id?: string;
  roomId: string;
  category?: AssetCategory | null;
  name: string;
  textNote?: string;
  icon?: string | undefined;
  uploadedFiles?: string[];
  hyperlinks?: AssetHyperlinks[];
  attachments?: Attachment[];
  events?: AssetEvent[];
  transformation?: TransformationInterface;
  positionDeleted?: boolean;
  matterTagContent?: string;
  stagesImportedNoteId?: string;
  scanTagId?: string;
  scanModelId?: string;
  scanId?: string;
}

export const UpdateAssetFile = {
  autoDetect: 1,
  image: 2,
  document: 3
} as const;

export type TUpdateAssetFile = keyof typeof UpdateAssetFile;

export const RecurringEventTime: Record<RecurringEventTimeType, TRecurringEventTimeValue> = {
  Day: { label: $localize`:@@GENERAL_DAY:Day`, value: 'Day' },
  Week: { label: $localize`:@@GENERAL_WEEK:Week`, value: 'Week' },
  Month: { label: $localize`:@@GENERAL_MONTH:Month`, value: 'Month' },
  Year: { label: $localize`:@@GENERAL_YEAR:Year`, value: 'Year' }
};

export type RecurringEventTimeType = 'Day' | 'Week' | 'Month' | 'Year';

export type TRecurringEventTimeValue = { label: string; value: RecurringEventTimeType };

export interface UploadEventsInterface {
  dateTime: Date | string;
  content: string;
  recurringEventType: TRecurringEventTime;
}

export interface DeleteAssetRequest {
  id: string;
  name: string;
}

export interface DeleteAssetHyperlinkInterface {
  hyperlinkGuid: string[];
}

export interface DeleteUploadedEventsInterface {
  fileGuid: string[];
}

export interface AssetEntryPrivileges {
  userRoleType: UserRoleType;
  assetPrivileges: string[];
}

export interface AssetPaginationItem
  extends Pick<TAsset, 'id' | 'name' | 'category' | 'roomId' | 'transformation' | 'positionDeleted'>, AssetMonitor {
  privileges?: AssetEntryPrivileges;
  roomName?: string;
  apartmentId?: string;
  apartmentName?: string;
  thumbnailUrlWithSasToken?: string;
}

export interface AssetPaginationEntry {
  item: AssetPaginationItem;
  privileges: AssetEntryPrivileges;
}

export type AssetPaginated = IPagination<AssetPaginationItem>;

export interface AssetPaginationItemExtended extends AssetPaginationItem {
  categoryItem: AssetCategoryCollectionInterface;
  canEdit: boolean;
  room: { id: string; name: string; color: string; isMasterRoom?: boolean };
}

export const AssetsSortBy = {
  None: 0,
  Name: 1,
  Category: 2,
  CreatedAt: 3,
  EditedAt: 4,
  Room: 5
} as const;

export type AssetSortByKeys = keyof typeof AssetsSortBy;
export type AssetSortByValues = EnumValueGetter<typeof AssetsSortBy>;
export type AssetSortBy = AssetSortByValues | AssetSortByKeys;

export interface AssetListFilters {
  roomIds: string[];
  apartmentIds: string[];
  categoryIds: string[];
  authorIds: string[];
  stagesImportedNotesIds: string[];
  lastEdited: LastEditedType | undefined;
  editedAtFrom: string | undefined;
  editedAtTo: string | undefined;
  sortBy: AssetSortBy;
  isAscending: boolean;
  name: string | undefined | null;
}

export type AssetListFiltersToCount = Pick<AssetListFilters, 'categoryIds' | 'lastEdited' | 'authorIds' | 'roomIds'>;

export interface AssetListFiltersWithPage extends AssetListFilters, IPaginationRequest {}

export interface AssetListFiltersOptions {
  /** Categories in use within the filtered scope (predefined and custom), as backend `AssetCategoryDto`. */
  categories: AssetCategory[];
  editedAtFrom: string;
  editedAtTo: string;
  authors: UserInfoCard[];
  rooms: AssetListFilterRoomOption[];
  apartments: AssetListFilterApartmentOption[];
}

export interface AssetListFilterRoomOption {
  roomId: string;
  roomName: string;
  roomColor: string | null;
  isMasterRoom: boolean;
}

export interface AssetListFilterApartmentOption {
  apartmentId: string;
  apartmentName: string;
}

export interface AssetCountPerRoom {
  roomId: string;
  assetCount: number;
}
